import React, { useState, useEffect, useRef } from 'react';
import Api from '../../../services/Api';
import { DetailsList, DetailsListLayoutMode, IColumn, SelectionMode, ConstrainMode, ColumnActionsMode, IconButton, Stack, Spinner, SpinnerSize, Link } from '@fluentui/react';
import ActionButtonsView from '../PowerPlatform/ActionButtonsView';
import TextFilter from '../Filters/TextFilter';
import DropdownTree from '../../CustomComponents/DropdownTree';
import PageNavi from '../PageNavi';
import ResultsOnPage from '../ResultsOnPage';
import axios from 'axios';
import DropdownFilter from '../Filters/DropdownFilter';
import ExportButton from '../ExportButton';
import { AppType, PowerAppBaseType, PowerAppConnectorTier, PowerAppProvisioningStatus, PowerAppsScope, PowerAppStatus } from '../../../GlobalConst/globalEnums';
import { createAndDownloadCsv, mapBooleanToFilterValues, mapEnumToFilterValues, renderDate, renderUser } from '../../../helpers/HelperMethods';
import InfoTooltip from '../../formFields/InfoTooltip';

const CancelToken = axios.CancelToken;

const PowerPlatformItemsList = ({filterBy, type}) => {

    const apiAddress = '/PowerAppsPanel';
    
    const isOwnerView = filterBy === 'owner'

    const defaultFilters = isOwnerView
        ? {
            inSolution: [false]
        }
        : {}

    const [isLoading, setIsLoading] = useState(true);
    const [isExporting, setIsExporting] = useState(false);
    const [powerApps, setPowerApps] = useState([]);
    const [orderBy, setOrderBy] = useState({ fieldName: '', isDescending: false});
    const [totalResults, setTotalResults] = useState(0);
    const [onPage, setOnPage] = useState(20);
    const [pageUrlFilter, setPageUrlFilter] = useState(1);
    const [filters, setFilters] = useState(defaultFilters);
    const cancelSource = useRef(null);


    useEffect(() => {
        return updateResults();
    }, [onPage, pageUrlFilter, filters, orderBy])

    const _onColumnClick = (ev, column) => {
        setOrderBy({
            fieldName: column.fieldName, 
            isDescending: !column.isSortedDescending
        });
    }
    //const renderUsers = (users) =>  users.map(x => renderUser(x))

    const renderConnectors = (connectors, tier) => connectors.filter(x => x.tier === tier).map(x => <div key={x.id}>{x.name}</div>)

    const loadApps = (apps) => {
        setPowerApps(apps);
        setIsLoading(false);
    }

    const onFilterChange = (fieldName, value) => {
        setFilters({
            ...filters,
            [fieldName]: value
        });
        setPageUrlFilter(1);
    }

    const handleNoSelection = () => {
        loadApps([]);
        setTotalResults(0);
    }

    const getResultsUrl = (isExport) => {
        let url = `${apiAddress}?`
        
        if (isExport)
            url += `forExport=1`;
        else
            url += `page=${pageUrlFilter}&onPage=${onPage}`;

        const filterUrl = Object.keys(filters).reduce((queryString, filterField) => {
            const filterText = filters[filterField];
            if(filterText) {
                queryString += `&${filterField}=${encodeURIComponent(filterText)}`;
            }
            return queryString;
        }, '');

        let orderByUrl = '';
        if(orderBy.fieldName) {
            orderByUrl = `&orderBy=${orderBy.fieldName}&isDesc=${orderBy.isDescending}`;
        }

        url = url + filterUrl + orderByUrl;

        if (filterBy) {
            url += `&filterBy=${filterBy}`
        }
        if (type) {
            url += `&type=${type}`
        }

        return url
    }

    const updateResults = () => {
        setIsLoading(true);       

        if (cancelSource.current) {
            cancelSource.current.cancel();
        }

        cancelSource.current = CancelToken.source();

        Api.get(getResultsUrl(false), {
            cancelToken: cancelSource.current.token
        })
        .then(res => res.data)
        .then(posts => {
            loadApps(posts.results);
            setTotalResults(posts.totalCount);
        })
        .catch(err => {});

        return () => {
            cancelSource.current.cancel();
        }
    }

    // const renderWithTooltip = (content, stringContent) => {
    //     return  <TooltipHost content={stringContent ? stringContent : content} overflowMode={TooltipOverflowMode.Parent} closeDelay={500}>
    //                 <span>{content}</span>
    //             </TooltipHost>;
    // }

    const initialColumns: IColumn[] = [
        {
            key: 'id',
            name: 'ID',
            fieldName: 'internalId',
            minWidth: 50,
            maxWidth: 300,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
            columnActionsMode: ColumnActionsMode.disabled
        },
        {
            key: 'name',
            name: 'Name',
            fieldName: 'name',
            minWidth: 150,
            maxWidth: 400,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
            onRender: (item) => <span title={item.name}>{item.name}</span>
        },
        {
            key: 'environment',
            name: 'Environment',
            fieldName: 'environment',
            minWidth: 150,
            maxWidth: 400,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
            onRender: (item) => <span title={item.environment.name}>{item.environment.name}</span>
        },
        {
            key: 'primaryOwner',
            name: 'Primary Owner',
            fieldName: 'primaryOwner',
            minWidth: 160,
            maxWidth: 200,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
            onRender: (item) => renderUser(item.primaryOwner)
        },
        {
            key: 'secondaryOwner',
            name: 'Secondary Owner',
            fieldName: 'secondaryOwner',
            minWidth: 160,
            maxWidth: 200,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
            onRender: (item) => renderUser(item.secondaryOwner)
        },
        {
            key: 'standardConnectors',
            name: 'Connectors (Standard)',
            fieldName: 'standardConnectors',
            minWidth: 80,
            maxWidth: 140,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
            onRender: (item) => renderConnectors(item.connectors, PowerAppConnectorTier.standard),
            columnActionsMode: ColumnActionsMode.disabled
        },
        {
            key: 'premiumConnectors',
            name: 'Connectors (Premium)',
            fieldName: 'premiumConnectors',
            minWidth: 80,
            maxWidth: 140,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
            onRender: (item) => renderConnectors(item.connectors, PowerAppConnectorTier.premium),
            columnActionsMode: ColumnActionsMode.disabled
        },
        {
            key: 'customConnectors',
            name: 'Connectors (Custom)',
            fieldName: 'customConnectors',
            minWidth: 80,
            maxWidth: 140,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
            onRender: (item) => renderConnectors(item.connectors, PowerAppConnectorTier.custom),
            columnActionsMode: ColumnActionsMode.disabled
        },
        {
            key: 'modified',
            name: 'Modified Date',
            fieldName: 'modified',
            minWidth: 70,
            maxWidth: 100,
            isRowHeader: false,
            isResizable: true,
            data: 'number',
            onRender: (item) => <span>{renderDate(item.modified)}</span>
        },
        {
            key: 'lastLaunched',
            name: 'Last Usage Date',
            fieldName: 'lastLaunched',
            minWidth: 70,
            maxWidth: 100,
            isRowHeader: false,
            isResizable: true,
            data: 'number',
            onRender: (item) => <span>{renderDate(item.lastLaunched)}</span>
        },
        {
            key: 'status',
            name: 'Status',
            fieldName: 'status',
            minWidth: 60,
            maxWidth: 150,
            isResizable: true,
            data: 'string'
        },
        {
            key: 'provisioningStatus',
            name: 'Technical Status',
            fieldName: 'provisioningStatus',
            minWidth: 60,
            maxWidth: 150,
            isResizable: true,
            data: 'string'
        },
        {
            key: 'management',
            name: 'Organization',
            fieldName: 'management',
            minWidth: 50,
            maxWidth: 100,
            isResizable: true,
            data: 'string',
            onRender: (item) => <span>{item.management && item.management.key}</span>
        },
        {
            key: 'geography',
            name: 'Country',
            fieldName: 'geography',
            minWidth: 50,
            maxWidth: 100,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
            onRender: (item) => <span>{item.geography && item.geography.value}</span>
        },
        {
            key: 'type',
            name: 'Type',
            fieldName: 'type',
            minWidth: 50,
            maxWidth: 80,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
        },
        {
            key: 'appType',
            name: 'App Type',
            fieldName: 'appType',
            minWidth: 60,
            maxWidth: 80,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
        },
        {
            key: 'inSolution',
            name: 'In Solution',
            fieldName: 'isPartOfSolution',
            minWidth: 40,
            maxWidth: 80,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
            onRender: (item) => <span>{item.isPartOfSolution ? 'Yes' : 'No'}</span>
        },
        {
            key: 'edit',
            name: 'Actions',
            minWidth: 64,
            maxWidth: 64,
            isResizable: false,
            isIconOnly: true,
            columnActionsMode: ColumnActionsMode.disabled,
            onRender: (item) => <ActionButtonsView editedItem={item} callBack={onGetAppCallback} />
        }
    ] 

    const ownerColumns = [
        {
            key: 'name',
            name: 'Name',
            fieldName: 'name',
            minWidth: 150,
            maxWidth: 400,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
            onRender: (item) => <Link title={item.name} href={item.url}>{item.name}</Link>
        },
        {
            key: 'environment',
            name: 'Environment',
            fieldName: 'environment',
            minWidth: 150,
            maxWidth: 400,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
            onRender: (item) => <span title={item.environment.name}>{item.environment.name}</span>
        },
        {
            key: 'primaryOwner',
            name: 'Primary Owner',
            fieldName: 'primaryOwner',
            minWidth: 160,
            maxWidth: 200,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
            onRender: (item) => renderUser(item.primaryOwner)
        },
        {
            key: 'secondaryOwner',
            name: 'Secondary Owner',
            fieldName: 'secondaryOwner',
            minWidth: 160,
            maxWidth: 200,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
            onRender: (item) => renderUser(item.secondaryOwner)
        },
        {
            key: 'standardConnectors',
            name: 'Connectors (Standard)',
            fieldName: 'standardConnectors',
            minWidth: 80,
            maxWidth: 140,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
            onRender: (item) => renderConnectors(item.connectors, PowerAppConnectorTier.standard),
            columnActionsMode: ColumnActionsMode.disabled
        },
        {
            key: 'premiumConnectors',
            name: 'Connectors (Premium)',
            fieldName: 'premiumConnectors',
            minWidth: 80,
            maxWidth: 140,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
            onRender: (item) => renderConnectors(item.connectors, PowerAppConnectorTier.premium),
            columnActionsMode: ColumnActionsMode.disabled
        },
        {
            key: 'customConnectors',
            name: 'Connectors (Custom)',
            fieldName: 'customConnectors',
            minWidth: 80,
            maxWidth: 140,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
            onRender: (item) => renderConnectors(item.connectors, PowerAppConnectorTier.custom),
            columnActionsMode: ColumnActionsMode.disabled
        },
        {
            key: 'appType',
            name: 'App Type',
            fieldName: 'appType',
            minWidth: 60,
            maxWidth: 80,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
        },
        {
            key: 'inSolution',
            name: 'In Solution',
            fieldName: 'isPartOfSolution',
            minWidth: 40,
            maxWidth: 80,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
            onRender: (item) => <span>{item.isPartOfSolution ? 'Yes' : 'No'}</span>
        },
        {
            key: 'provisioningStatus',
            name: 'Technical Status',
            fieldName: 'provisioningStatus',
            minWidth: 60,
            maxWidth: 150,
            isResizable: true,
            data: 'string'
        },
        {
            key: 'edit',
            name: 'Actions',
            minWidth: 64,
            maxWidth: 64,
            isResizable: false,
            isIconOnly: true,
            columnActionsMode: ColumnActionsMode.disabled,
            onRender: (item) => <ActionButtonsView editedItem={item} callBack={onGetAppCallback} filterBy={filterBy} />
        }
    ]

    const onGetAppCallback = async app => {
        const updatedItems = powerApps.map(x => (x.id === app.id) ? app : x);
        setPowerApps(updatedItems);
    }

    
    
    const exportApps = () => {
        const exportFields = isOwnerView
            ? [{
                label: 'Name',
                value: 'name'
            }, {
                label: 'Environment',
                value: 'environment'
            }, {
                label: 'Primary Owner',
                value: 'primaryOwner'
            }, {
                label: 'Secondary Owner',
                value: 'secondaryOwner'
            }, {
                label: 'Connectors (Standard)',
                value: 'standardConnectors'
            }, {
                label: 'Connectors (Premium)',
                value: 'premiumConnectors'
            }, {
                label: 'Connectors (Custom)',
                value: 'customConnectors'
            }, {
                label: 'App Type',
                value: 'appType'
            }, {
                label: 'In Solution',
                value: 'isPartOfSolution'
            }, { 
                label: 'Technical Status',
                value: 'provisioningStatus'
            }]   
            : [{
                label: 'ID',
                value: 'internalId'
            },{
                label: 'Name',
                value: 'name'
            }, {
                label: 'Environment',
                value: 'environment'
            }, {
                label: 'Primary Owner',
                value: 'primaryOwner'
            }, {
                label: 'Secondary Owner',
                value: 'secondaryOwner'
            }, {
                label: 'Connectors (Standard)',
                value: 'standardConnectors'
            }, {
                label: 'Connectors (Premium)',
                value: 'premiumConnectors'
            }, {
                label: 'Connectors (Custom)',
                value: 'customConnectors'
            }, {
                label: 'Modified',
                value: 'modified'
            }, {
                label: 'Last Usage Date',
                value: 'lastLaunched'
            }, {
                label: 'Status',
                value: 'status'
            },{ 
                label: 'Technical Status',
                value: 'provisioningStatus'
            },{ 
                label: 'Organization',
                value: 'organization'
            }, {
                label: 'Country',
                value: 'country'
            }, {
                label: 'App Type',
                value: 'appType'
            }, {
                label: 'Type',
                value: 'type'
            }, {
                label: 'In Solution',
                value: 'isPartOfSolution'
            }];


        setIsExporting(true);

        if (cancelSource.current) {
            cancelSource.current.cancel();
        }

        cancelSource.current = CancelToken.source();

        Api.get(getResultsUrl(true), {
            cancelToken: cancelSource.current.token
        })
        .then(res => res.data)
        .then(posts => {
            createAndDownloadCsv(posts, type ? type + "s" : "powerApps", exportFields);
            setIsExporting(false);
        })
        .catch(err => {});

        return () => {
            cancelSource.current.cancel();
        }     
    }

    const getColumns = (initialColumns) => {
 
        const columns = initialColumns.map(c => {
            c.isFiltered = (filters[c.fieldName] || '').length > 0;
            c.isSorted = orderBy.fieldName === c.fieldName;
            c.isSortedDescending = c.isSorted ? orderBy.isDescending : true;
            return c;
        });

        return columns;
    }
    
    const columns = getColumns(isOwnerView ? ownerColumns : initialColumns);
    const spinner = <Spinner style={{margin: '200px'}} size={SpinnerSize.large} />;

    const statusTooltip =
        <div>
            Indicates the status of the app / flow / solution:
            <ul>
                <li>Active – The app or flow is currently available (suspended and turned off flows are also considered "Active" as they can be turned On by owners).</li>
                <li>Quarantined – The app or flow has been restricted due to policy violations, issues or lifecycle.</li>
                <li>Deleted – The app or flow has been removed and is no longer available.</li>
            </ul>
        </div> 
    
    const provisioningStatusTooltip =
        <div>
            Indicates the technical status of the app / flow / solution:
            <ul>
                <li>Ready – Fully functional and available for changes.</li>
                <li>Updating – Currently undergoing changes or updates.</li>
                <li>Failed – An error occurred during the last operation, it will be retried automatically.</li>
                <li>Permanently Failed – A critical failure occurred that cannot be automatically recovered, require action from Provisioner admin.</li>
                <li>No Access – Not accessible by users but still exist and possible to restore.</li>
                <li>Deleted – The item has been removed and is no longer available.</li>
                <li>Pending Approval – Awaiting approval decision.</li>
                <li>Partially Failed – Some components failed, but others may still be working.</li>
                <li>Expired – The item is no longer active due to recertification expiration.</li>
            </ul>
        </div>

    const isWhitelistedFilterTooltip = 
        <div>
            Determines if solution/app/flow is included in lifecycle and visibility.
            <ul>
                <li><b>Yes</b> – This item is <b>whitelisted</b>. It is <b>hidden from end users</b> and <b>excluded from lifecycle management</b>.</li>
                <li><b>No</b> – This item is <b>not whitelisted</b>. It is <b>visible to end users</b> and <b>included in lifecycle management</b>.</li>
            </ul>
        </div>

    const filtersFields =
        <div className="filters">
            <div className="cell">
                <TextFilter
                    fieldName='name'
                    fieldLabel='Name / ID'
                    onChange={onFilterChange}
                    />
            </div>
            <div className="cell">
                <TextFilter
                    fieldName='environment'
                    fieldLabel='Environment'
                    onChange={onFilterChange}
                />
            </div>
            <div className="cell">
                <TextFilter
                    fieldName='primaryOwner'
                    fieldLabel='Primary Owner'
                    onChange={onFilterChange}
                />
            </div>
            <div className="cell">
                <TextFilter
                    fieldName='secondaryOwner'
                    fieldLabel='Secondary Owner'
                    onChange={onFilterChange}
                />
            </div>
            <div className="cell">
                <TextFilter
                    fieldName='standardConnectors'
                    fieldLabel='Connectors (Standard)'
                    onChange={onFilterChange}
                />
            </div>
            <div className="cell">
                <TextFilter
                    fieldName='premiumConnectors'
                    fieldLabel='Connectors (Premium)'
                    onChange={onFilterChange}
                />
            </div>
            <div className="cell">
                <TextFilter
                    fieldName='customConnectors'
                    fieldLabel='Connectors (Custom)'
                    onChange={onFilterChange}
                />
            </div>
            <div className="cell">
                <DropdownFilter
                    fieldName="connectors"
                    values={mapEnumToFilterValues(PowerAppConnectorTier)}
                    title='Used Connectors'
                    onChange={onFilterChange}
                    defaultEmpty={true}
                />
            </div>
            <div className="cell">
                <DropdownTree
                    address={'/Management?parent='}
                    fieldName='organization'
                    label='Busines Owner Organization'
                    multiselect={false}
                    onChange={onFilterChange}
                />
            </div>
            <div className="cell">
                <DropdownTree
                    address={'/Geography?parent='}
                    fieldName='siteAudience'
                    label='Country'
                    multiselect={true}
                    onChange={onFilterChange}
                    includeCrossOrg={true}
                />
            </div>
            <div className="cell">
                <DropdownFilter
                    fieldName="status"
                    values={mapEnumToFilterValues(PowerAppStatus)}
                    title={<span>Status <InfoTooltip content={statusTooltip} /></span>}
                    onChange = {onFilterChange}
                    noselection = {handleNoSelection}
                    includeSelectAll = {true}
                />
            </div>
            <div className="cell">
                <DropdownFilter
                    fieldName="provisioningStatus"
                    values={mapEnumToFilterValues(PowerAppProvisioningStatus)}
                    title={<span>Technical Status <InfoTooltip content={provisioningStatusTooltip} /></span>}
                    onChange = {onFilterChange}
                    noselection = {handleNoSelection}
                    includeSelectAll = {true}
                />
            </div>
            <div className="cell">
                <DropdownFilter
                    fieldName="appType"
                    values={mapEnumToFilterValues(AppType)}
                    title='App Type'
                    onChange = {onFilterChange}
                    noselection = {handleNoSelection}
                    includeSelectAll = {true}
                    includeEmpty = {true}
                />
            </div>
            <div className="cell">
                <DropdownFilter
                    fieldName="type"
                    values={mapEnumToFilterValues(PowerAppBaseType)}
                    title='Type'
                    onChange = {onFilterChange}
                    noselection = {handleNoSelection}
                    includeSelectAll = {false}
                />
            </div>
            <div className="cell">
                <DropdownFilter
                    fieldName="inSolution"
                    values={mapBooleanToFilterValues()}
                    title='In Solution'
                    onChange={onFilterChange}
                    noselection={handleNoSelection}
                />
            </div>                            
            <div className="cell">
                <TextFilter
                    fieldName='description'
                    fieldLabel='Description'
                    onChange={onFilterChange}
                    tooltip='Separate keywords that you want to search with comma (,).'
                />
            </div>
            {
                filterBy !== 'environmentOwner' &&
                <div className="cell">
                    <DropdownFilter
                        fieldName="isWhitelisted"
                        values={mapBooleanToFilterValues()}
                        title={<span>Is Whitelisted <InfoTooltip content={isWhitelistedFilterTooltip}/></span>}
                        onChange = {onFilterChange}
                        noselection = {handleNoSelection}
                    />
                </div>
            }
        </div>

    const ownerFiltersFields = 
        <div className="filters">
            <div className="cell">
                <TextFilter
                    fieldName='name'
                    fieldLabel='Name / ID'
                    onChange={onFilterChange}
                    />
            </div>
            <div className="cell">
                <TextFilter
                    fieldName='environment'
                    fieldLabel='Environment'
                    onChange={onFilterChange}
                />
            </div>
            <div className="cell">
                <TextFilter
                    fieldName='primaryOwner'
                    fieldLabel='Primary Owner'
                    onChange={onFilterChange}
                />
            </div>
            <div className="cell">
                <TextFilter
                    fieldName='secondaryOwner'
                    fieldLabel='Secondary Owner'
                    onChange={onFilterChange}
                />
            </div>
            {
                type !== 'agent' &&
                <div className="cell">
                    <DropdownFilter
                        fieldName="inSolution"
                        values={mapBooleanToFilterValues()}
                        title='In Solution'
                        onChange={onFilterChange}
                        noselection={handleNoSelection}
                        defaultValues={defaultFilters.inSolution}
                    />
                </div> 
            }
            {
                type === PowerAppBaseType.powerApp &&
                <div className="cell">
                    <DropdownFilter
                        fieldName="appType"
                        values={mapEnumToFilterValues(AppType)}
                        title='App Type'
                        onChange = {onFilterChange}
                        noselection = {handleNoSelection}
                        includeSelectAll = {true}
                        includeEmpty = {true}
                    />
                </div> 
            }
            <div className="cell">
                <DropdownFilter
                    fieldName="connectors"
                    values={mapEnumToFilterValues(PowerAppConnectorTier)}
                    title='Used Connectors'
                    onChange={onFilterChange}
                    defaultEmpty={true}
                />
            </div>
            <div className="cell">
                <TextFilter
                    fieldName='allConnectors'
                    fieldLabel='Connectors'
                    onChange={onFilterChange}
                />
            </div>
            <div className="cell">
                <DropdownFilter
                    fieldName="scope"
                    values={mapEnumToFilterValues(PowerAppsScope)}
                    title='Scope'
                    onChange = {onFilterChange}
                    noselection = {handleNoSelection}
                    includeEmpty={true}
                />
            </div>
        </div>

    return (
        <div>
            <div>
                {
                    isOwnerView
                        ? ownerFiltersFields
                        : filtersFields
                }
                <div style={{position:'relative', margin: '20px 0'}}>
                    <div>
                        <IconButton 
                            style={{color: 'rgb(96, 94, 92)', position: 'absolute'}} 
                            onClick={updateResults}
                            iconProps={{iconName: 'refresh'}} 
                            />
                        <PageNavi
                            pageNumPart = {setPageUrlFilter}
                            totalResults = {totalResults}
                            resultsOnPage = {onPage}
                            pageNum = {pageUrlFilter}
                            showTotalResults = {true}
                            />
                        
                        <Stack horizontal disableShrink>
                            <ResultsOnPage
                                onPage = {onPage}
                                onPageResults = {setOnPage}
                                />
                            <ExportButton 
                                onClick = {exportApps}
                                loading = {isExporting}
                                >                           
                            </ExportButton>
                        </Stack>                            
                    </div>
                </div>
            </div>
            <div>
                {
                    isLoading 
                    ? spinner                  
                    : <DetailsList
                        items={powerApps}
                        columns={columns}
                        layoutMode={DetailsListLayoutMode.justified}
                        isHeaderVisible={true}
                        selectionMode={SelectionMode.none}
                        styles={{root: {width: '100%'}}}
                        onColumnHeaderClick={_onColumnClick}
                        constrainMode={ConstrainMode.unconstrained}
                        />
                }
            </div>
        </div>
        )
    }


export default PowerPlatformItemsList;


// WEBPACK FOOTER //
// ./src/components/Administration/PowerPlatform/PowerPlatformItemsList.js